import { supabase } from '../../supabase';
import type { UserProfile, UserStats, UserActivity, PaginatedResponse } from './types';

export async function getUserProfile(userId: string): Promise<UserProfile> {
  // Get profile data with follower counts
  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select(`
      id,
      username,
      avatar_url,
      created_at,
      followers_count,
      following_count,
      is_following
    `)
    .eq('id', userId)
    .single();

  if (error) {
    console.error("Error fetching user profile:", error);
    throw new Error('Failed to fetch user profile');
  }

  if (!profile) {
    throw new Error('User profile not found');
  }

  // Get real-time follower counts
  const [{ count: followersCount }, { count: followingCount }] = await Promise.all([
    supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', userId),
    supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', userId)
  ]);

  // Get current user's follow status
  const { data: { user } } = await supabase.auth.getUser();
  const { data: followStatus } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', user?.id)
    .eq('following_id', userId)
    .maybeSingle();

  return {
    ...profile,
    followers_count: followersCount || 0,
    following_count: followingCount || 0,
    is_following: !!followStatus
  };
}

export async function updateProfile(username: string | null, avatarUrl: string | null): Promise<void> {
  console.log("updateProfile called with username:", username, "and avatarUrl:", avatarUrl);
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // First, get the current profile to ensure we have the username
  if (username === null) {
    const { data: currentProfile } = await supabase
      .from('profiles')
      .select('username')
      .eq('user_id', user.id)
      .single();
    
    if (currentProfile) {
      username = currentProfile.username;
      console.log("Retrieved current username:", username);
    } else {
      throw new Error('Profile not found');
    }
  }

  // Check if username is already taken (excluding current user)
  if (username !== null) {
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', username)
      .neq('user_id', user.id)
      .maybeSingle();

    if (existingUser) {
      throw new Error('username_key');
    }
  }

  // Prepare update object with explicit avatar_url field
  const updates: Record<string, any> = {
    updated_at: new Date().toISOString()
  };

  // Only include username if it should be updated
  if (username !== null) {
    updates.username = username;
  }

  // Always include avatar_url in the update, even if it's null
  updates.avatar_url = avatarUrl;

  console.log("Updating profile with:", updates);

  // Update profile with direct query to ensure avatar_url is updated
  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('user_id', user.id);

  if (error) {
    console.error("Profile update error:", error);
    throw error;
  }
  
  console.log("Profile updated successfully");
}